export interface KakaoLatLng {
  getLat(): number;
  getLng(): number;
}

export interface KakaoMap {
  setCenter(center: KakaoLatLng): void;
  panTo(center: KakaoLatLng): void;
  getLevel(): number;
  setLevel(level: number, options?: Record<string, unknown>): void;
  addControl(control: object, position: unknown): void;
}

export interface KakaoOverlay {
  setMap(map: KakaoMap | null): void;
}

export interface KakaoPlaceItem {
  id: string;
  place_name: string;
  category_name: string;
  category_group_code: string;
  category_group_name: string;
  phone: string;
  address_name: string;
  road_address_name: string;
  x: string; // lng
  y: string; // lat
  place_url: string;
  distance: string;
}

export type KakaoSearchStatus = 'OK' | 'ZERO_RESULT' | 'ERROR';

export interface KakaoPlacesService {
  categorySearch(
    categoryGroupCode: string,
    callback: (result: KakaoPlaceItem[], status: KakaoSearchStatus) => void,
    options?: {
      location?: KakaoLatLng;
      radius?: number;
      size?: number;
      page?: number;
    }
  ): void;
  keywordSearch(
    keyword: string,
    callback: (result: KakaoPlaceItem[], status: KakaoSearchStatus) => void,
    options?: {
      location?: KakaoLatLng;
      radius?: number;
      size?: number;
      page?: number;
    }
  ): void;
}

export interface KakaoAddressItem {
  address?: {
    address_name: string;
    region_1depth_name: string;
    region_2depth_name: string;
    region_3depth_name: string;
    main_address_no: string;
    sub_address_no: string;
    zip_code: string;
  };
  road_address?: {
    address_name: string;
    region_1depth_name: string;
    region_2depth_name: string;
    region_3depth_name: string;
    road_name: string;
    building_name: string;
    main_building_no: string;
    sub_building_no: string;
    zone_no: string;
  };
}

export interface KakaoGeocoderService {
  coord2Address(
    x: number,
    y: number,
    callback: (result: KakaoAddressItem[], status: KakaoSearchStatus) => void
  ): void;
  coord2RegionCode(
    x: number,
    y: number,
    callback: (result: Array<{ region_type: string; address_name: string; region_1depth_name: string; region_2depth_name: string; region_3depth_name: string }>, status: KakaoSearchStatus) => void
  ): void;
  addressSearch(
    addr: string,
    callback: (result: Array<{ address_name: string; road_address_name: string; x: string; y: string }>, status: KakaoSearchStatus) => void
  ): void;
}

export interface KakaoMapsApi {
  maps: {
    load(callback: () => void): void;
    Map: new (container: HTMLElement, options: Record<string, unknown>) => KakaoMap;
    LatLng: new (lat: number, lng: number) => KakaoLatLng;
    Marker: new (options: Record<string, unknown>) => KakaoOverlay;
    Circle: new (options: Record<string, unknown>) => KakaoOverlay;
    ZoomControl: new () => object;
    ControlPosition: { TOPRIGHT: unknown };
    event: { addListener(target: object, eventName: string, listener: () => void): void };
    services: {
      Places: new () => KakaoPlacesService;
      Geocoder: new () => KakaoGeocoderService;
      Status: {
        OK: 'OK';
        ZERO_RESULT: 'ZERO_RESULT';
        ERROR: 'ERROR';
      };
    };
  };
}

declare global {
  interface Window {
    kakao?: KakaoMapsApi;
    __kakaoMapsLoadPromise?: Promise<KakaoMapsApi>;
  }
}

export function loadKakaoMaps(appKey: string): Promise<KakaoMapsApi> {
  if (window.kakao?.maps?.Map && window.kakao?.maps?.services) return Promise.resolve(window.kakao);
  if (window.__kakaoMapsLoadPromise) return window.__kakaoMapsLoadPromise;

  window.__kakaoMapsLoadPromise = new Promise<KakaoMapsApi>((resolve, reject) => {
    const script = document.createElement('script');
    const timeout = window.setTimeout(() => {
      window.__kakaoMapsLoadPromise = undefined;
      reject(new Error('Kakao Maps konnte nicht rechtzeitig geladen werden.'));
    }, 10_000);

    const fail = (message: string) => {
      window.clearTimeout(timeout);
      window.__kakaoMapsLoadPromise = undefined;
      reject(new Error(message));
    };

    script.async = true;
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?autoload=false&appkey=${encodeURIComponent(
      appKey
    )}&libraries=services,clusterer,drawing`;

    script.onload = () => {
      if (!window.kakao?.maps) {
        fail('Kakao Maps wurde geladen, aber nicht initialisiert.');
        return;
      }
      window.kakao.maps.load(() => {
        window.clearTimeout(timeout);
        resolve(window.kakao as KakaoMapsApi);
      });
    };
    script.onerror = () =>
      fail(
        'Das Kakao-Maps-Skript konnte nicht geladen werden. Prüfe deinen JavaScript App Key und die Web-Domain in der Kakao Konsole.'
      );
    document.head.appendChild(script);
  });

  return window.__kakaoMapsLoadPromise;
}
